export type LicenseType = 'trial' | 'free' | 'monthly' | 'annual' | 'lifetime' | 'enterprise';

export interface LicenseTier {
  id: string;
  name: string;
  type: LicenseType;
  description: string;
  active: boolean;
  visible: boolean;
  color: string;
  createdAt: string;
  updatedAt: string;

  // Pricing
  priceMonthly: number;
  priceAnnual: number;
  priceLifetime: number;
  trialDays: number;

  // Limits
  maxRadios: number | null;
  maxUsers: number | null;
  storageGB: number;
  bandwidthGB: number;
  streamingBitrateKbps: number | null;

  // Feature toggles
  marketplace: boolean;
  tv: boolean;
  apps: boolean;
  ai: boolean;
  advertising: boolean;
  analytics: boolean;
  streaming: boolean;
  api: boolean;
  customDomain: boolean;
  verification: boolean;
  frequencyPreferred: boolean;

  // Feature flags
  featureFlags: string[];
}

export interface LicenseHistoryEntry {
  id: string;
  licenseId: string;
  action: 'created' | 'updated' | 'duplicated' | 'cloned' | 'activated' | 'deactivated' | 'published' | 'hidden';
  user: string;
  timestamp: string;
  detail: string;
}

export const licenseTiers: LicenseTier[] = [
  {
    id: 'lic_trial',
    name: 'Trial',
    type: 'trial',
    description: 'Período de prueba gratuito con acceso completo por tiempo limitado. Ideal para que nuevas radios exploren la plataforma sin compromiso.',
    active: true,
    visible: true,
    color: '#16a34a',
    createdAt: '2025-01-15',
    updatedAt: '2026-07-10',
    priceMonthly: 0,
    priceAnnual: 0,
    priceLifetime: 0,
    trialDays: 14,
    maxRadios: 1,
    maxUsers: 2,
    storageGB: 2,
    bandwidthGB: 10,
    streamingBitrateKbps: 128,
    marketplace: false,
    tv: false,
    apps: false,
    ai: false,
    advertising: false,
    analytics: false,
    streaming: true,
    api: false,
    customDomain: false,
    verification: false,
    frequencyPreferred: false,
    featureFlags: ['ff_streaming_basic'],
  },
  {
    id: 'lic_free',
    name: 'Free',
    type: 'free',
    description: 'Plan gratuito permanente con funcionalidades esenciales. Perfecto para radios comunitarias o proyectos personales que recién comienzan.',
    active: true,
    visible: true,
    color: '#78716c',
    createdAt: '2025-01-15',
    updatedAt: '2026-07-10',
    priceMonthly: 0,
    priceAnnual: 0,
    priceLifetime: 0,
    trialDays: 0,
    maxRadios: 1,
    maxUsers: 3,
    storageGB: 5,
    bandwidthGB: 25,
    streamingBitrateKbps: 128,
    marketplace: false,
    tv: false,
    apps: false,
    ai: false,
    advertising: true,
    analytics: true,
    streaming: true,
    api: false,
    customDomain: false,
    verification: false,
    frequencyPreferred: false,
    featureFlags: ['ff_streaming_basic', 'ff_analytics_viewer', 'ff_ads_basic'],
  },
  {
    id: 'lic_monthly',
    name: 'Mensual',
    type: 'monthly',
    description: 'Licencia de pago mensual con herramientas avanzadas. La opción más flexible para radios que quieren crecer sin ataduras a largo plazo.',
    active: true,
    visible: true,
    color: '#0ea5e9',
    createdAt: '2025-02-01',
    updatedAt: '2026-07-10',
    priceMonthly: 29,
    priceAnnual: 0,
    priceLifetime: 0,
    trialDays: 7,
    maxRadios: 3,
    maxUsers: 10,
    storageGB: 50,
    bandwidthGB: 200,
    streamingBitrateKbps: 192,
    marketplace: true,
    tv: false,
    apps: true,
    ai: false,
    advertising: true,
    analytics: true,
    streaming: true,
    api: true,
    customDomain: true,
    verification: true,
    frequencyPreferred: false,
    featureFlags: ['ff_streaming_hd', 'ff_analytics_pro', 'ff_ads_advanced', 'ff_marketplace_seller', 'ff_api_v1', 'ff_custom_domain'],
  },
  {
    id: 'lic_annual',
    name: 'Anual',
    type: 'annual',
    description: 'Licencia anual con descuento significativo y todas las herramientas profesionales. La mejor relación costo-beneficio para radios establecidas.',
    active: true,
    visible: true,
    color: '#d97706',
    createdAt: '2025-02-01',
    updatedAt: '2026-07-10',
    priceMonthly: 0,
    priceAnnual: 290,
    priceLifetime: 0,
    trialDays: 14,
    maxRadios: 10,
    maxUsers: 50,
    storageGB: 250,
    bandwidthGB: 1000,
    streamingBitrateKbps: 320,
    marketplace: true,
    tv: true,
    apps: true,
    ai: true,
    advertising: true,
    analytics: true,
    streaming: true,
    api: true,
    customDomain: true,
    verification: true,
    frequencyPreferred: true,
    featureFlags: ['ff_streaming_hd', 'ff_analytics_pro', 'ff_ads_advanced', 'ff_marketplace_seller', 'ff_marketplace_buyer', 'ff_api_v1', 'ff_custom_domain', 'ff_ai_moderation', 'ff_ai_scheduling', 'ff_tv_channel'],
  },
  {
    id: 'lic_lifetime',
    name: 'Lifetime',
    type: 'lifetime',
    description: 'Pago único de por vida con acceso ilimitado a todas las funcionalidades. La solución definitiva para emisoras que apuestan por el largo plazo.',
    active: true,
    visible: true,
    color: '#7c3aed',
    createdAt: '2025-06-01',
    updatedAt: '2026-07-10',
    priceMonthly: 0,
    priceAnnual: 0,
    priceLifetime: 1999,
    trialDays: 0,
    maxRadios: null,
    maxUsers: null,
    storageGB: 1024,
    bandwidthGB: 5000,
    streamingBitrateKbps: 320,
    marketplace: true,
    tv: true,
    apps: true,
    ai: true,
    advertising: true,
    analytics: true,
    streaming: true,
    api: true,
    customDomain: true,
    verification: true,
    frequencyPreferred: true,
    featureFlags: ['ff_streaming_hd', 'ff_analytics_pro', 'ff_ads_advanced', 'ff_marketplace_seller', 'ff_marketplace_buyer', 'ff_api_v2', 'ff_custom_domain', 'ff_ai_moderation', 'ff_ai_scheduling', 'ff_ai_content_gen', 'ff_tv_channel', 'ff_white_label'],
  },
  {
    id: 'lic_enterprise',
    name: 'Enterprise',
    type: 'enterprise',
    description: 'Solución corporativa totalmente personalizada con soporte dedicado, SLAs, integraciones a medida y facturación bajo contrato. Para grandes grupos de radiodifusión.',
    active: true,
    visible: true,
    color: '#1c1917',
    createdAt: '2025-09-01',
    updatedAt: '2026-07-10',
    priceMonthly: 0,
    priceAnnual: 0,
    priceLifetime: 0,
    trialDays: 0,
    maxRadios: null,
    maxUsers: null,
    storageGB: 5120,
    bandwidthGB: null,
    streamingBitrateKbps: null,
    marketplace: true,
    tv: true,
    apps: true,
    ai: true,
    advertising: true,
    analytics: true,
    streaming: true,
    api: true,
    customDomain: true,
    verification: true,
    frequencyPreferred: true,
    featureFlags: ['ff_streaming_hd', 'ff_analytics_enterprise', 'ff_ads_enterprise', 'ff_marketplace_enterprise', 'ff_api_v2', 'ff_custom_domain', 'ff_ai_full_suite', 'ff_tv_channel', 'ff_white_label', 'ff_dedicated_support', 'ff_custom_integrations', 'ff_sla_guaranteed'],
  },
];

export const licenseHistory: LicenseHistoryEntry[] = [
  { id: 'h1', licenseId: 'lic_trial', action: 'updated', user: 'admin@rfw.com', timestamp: '2026-07-10T14:22:00Z', detail: 'Trial extendido de 7 a 14 días' },
  { id: 'h2', licenseId: 'lic_monthly', action: 'updated', user: 'admin@rfw.com', timestamp: '2026-07-08T09:15:00Z', detail: 'Storage aumentado de 30 GB a 50 GB' },
  { id: 'h3', licenseId: 'lic_enterprise', action: 'updated', user: 'superadmin@rfw.com', timestamp: '2026-07-05T16:40:00Z', detail: 'Agregados feature flags: ff_sla_guaranteed, ff_custom_integrations' },
  { id: 'h4', licenseId: 'lic_annual', action: 'published', user: 'admin@rfw.com', timestamp: '2026-07-01T11:00:00Z', detail: 'Licencia Anual publicada en pricing page' },
  { id: 'h5', licenseId: 'lic_lifetime', action: 'duplicated', user: 'superadmin@rfw.com', timestamp: '2026-06-28T10:30:00Z', detail: 'Copia creada desde licencia Anual como base' },
  { id: 'h6', licenseId: 'lic_free', action: 'activated', user: 'admin@rfw.com', timestamp: '2026-06-20T08:00:00Z', detail: 'Licencia Free activada para nuevas radios' },
  { id: 'h7', licenseId: 'lic_lifetime', action: 'created', user: 'superadmin@rfw.com', timestamp: '2025-06-01T12:00:00Z', detail: 'Licencia Lifetime creada' },
  { id: 'h8', licenseId: 'lic_enterprise', action: 'created', user: 'superadmin@rfw.com', timestamp: '2025-09-01T09:00:00Z', detail: 'Licencia Enterprise creada' },
  { id: 'h9', licenseId: 'lic_trial', action: 'created', user: 'admin@rfw.com', timestamp: '2025-01-15T10:00:00Z', detail: 'Licencia Trial creada' },
  { id: 'h10', licenseId: 'lic_free', action: 'created', user: 'admin@rfw.com', timestamp: '2025-01-15T10:05:00Z', detail: 'Licencia Free creada' },
  { id: 'h11', licenseId: 'lic_monthly', action: 'created', user: 'admin@rfw.com', timestamp: '2025-02-01T14:00:00Z', detail: 'Licencia Mensual creada' },
  { id: 'h12', licenseId: 'lic_annual', action: 'created', user: 'admin@rfw.com', timestamp: '2025-02-01T14:30:00Z', detail: 'Licencia Anual creada' },
];

export function getLicenseById(id: string): LicenseTier | undefined {
  return licenseTiers.find((l) => l.id === id);
}

export function getLicenseHistory(licenseId: string): LicenseHistoryEntry[] {
  return licenseHistory.filter((h) => h.licenseId === licenseId);
}

export function formatStorage(gb: number): string {
  if (gb >= 1000) return `${(gb / 1024).toFixed(1)} TB`;
  return `${gb} GB`;
}

export function formatBandwidth(gb: number | null): string {
  if (gb === null) return 'Ilimitado';
  if (gb >= 1000) return `${(gb / 1024).toFixed(1)} TB/mes`;
  return `${gb} GB/mes`;
}

export function formatUsers(n: number | null): string {
  if (n === null) return 'Ilimitado';
  return n.toLocaleString();
}

export function formatRadios(n: number | null): string {
  if (n === null) return 'Ilimitado';
  return n.toLocaleString();
}

export function formatBitrate(kbps: number | null): string {
  if (kbps === null) return 'Custom';
  return `${kbps} kbps`;
}

export const licenseTypeLabels: Record<LicenseType, string> = {
  trial: 'Trial',
  free: 'Free',
  monthly: 'Mensual',
  annual: 'Anual',
  lifetime: 'Lifetime',
  enterprise: 'Enterprise',
};

export const licenseTypeIcons: Record<LicenseType, string> = {
  trial: 'ri-timer-line',
  free: 'ri-gift-line',
  monthly: 'ri-calendar-check-line',
  annual: 'ri-calendar-2-line',
  lifetime: 'ri-vip-crown-line',
  enterprise: 'ri-building-2-line',
};

export const licenseTypeBadge: Record<LicenseType, string> = {
  trial: 'bg-emerald-50 text-emerald-700',
  free: 'bg-background-200 text-foreground-600',
  monthly: 'bg-sky-50 text-sky-700',
  annual: 'bg-amber-50 text-amber-700',
  lifetime: 'bg-violet-50 text-violet-700',
  enterprise: 'bg-foreground-950 text-background-50',
};